import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { DailySchedule } from '../types/schedule';
import { todayStr } from '../utils/date';
import type { Child } from '../types/child';
import { logBetaEvent, logBetaIssue } from '../lib/beta-ops';

type GenerateScheduleFn = (child: Child, date: string) => DailySchedule;
let _generateSchedule: GenerateScheduleFn | null = null;
import('../engine/scheduler').then(m => { _generateSchedule = m.generateSchedule; });

interface ScheduleState {
  schedules: Record<string, DailySchedule>; // keyed by "date-childId"
  getOrCreateSchedule: (child: Child, date?: string) => DailySchedule;
  updateSchedule: (schedule: DailySchedule) => void;
  markActivityStatus: (
    scheduleId: string,
    blockId: string,
    activityId: string,
    status: 'pending' | 'in-progress' | 'completed' | 'skipped',
    stars?: 1 | 2 | 3,
    xp?: number,
  ) => DailySchedule | null;
  markEnrichmentComplete: (scheduleId: string, enrichmentId: string) => DailySchedule | null;
  skipEnrichment: (scheduleId: string, enrichmentId: string) => DailySchedule | null;
}

export const useScheduleStore = create<ScheduleState>()(
  persist((set, get) => ({
    schedules: {},

    getOrCreateSchedule: (child, date) => {
      const d = date || todayStr();
      const key = `${d}-${child.id}`;
      const existing = get().schedules[key];
      if (existing) return existing;

      let schedule: DailySchedule;
      try {
        if (!_generateSchedule) throw new Error('Schedule engine still loading');
        schedule = _generateSchedule(child, d);
      } catch (error) {
        logBetaIssue('schedule.generate', error, { childId: child.id, date: d });
        throw error;
      }
      set(state => ({
        schedules: { ...state.schedules, [key]: schedule },
      }));
      logBetaEvent('schedule.generated', {
        childId: child.id,
        date: d,
        blockCount: schedule.timeBlocks.length,
        totalMinutes: schedule.totalMinutes,
      });
      return schedule;
    },

    updateSchedule: (schedule) => {
      set(state => ({
        schedules: { ...state.schedules, [schedule.id]: schedule },
      }));
    },

    markActivityStatus: (scheduleId, blockId, activityId, status, stars, xp) => {
      const schedule = get().schedules[scheduleId];
      if (!schedule) return null;

      const updatedBlocks = schedule.timeBlocks.map(block => {
        if (block.id !== blockId) return block;
        return {
          ...block,
          activities: block.activities.map(a => {
            if (a.activityId !== activityId) return a;
            return {
              ...a,
              status,
              startedAt: status === 'in-progress' ? new Date().toISOString() : a.startedAt,
              completedAt: status === 'completed' ? new Date().toISOString() : a.completedAt,
              starsEarned: stars ?? a.starsEarned,
              xpAwarded: xp ?? a.xpAwarded,
            };
          }),
        };
      });

      const completedMinutes = updatedBlocks.reduce((sum, block) => {
        const allDone = block.activities.every(a => a.status === 'completed' || a.status === 'skipped');
        return sum + (allDone ? block.durationMinutes : 0);
      }, 0);

      const updated: DailySchedule = {
        ...schedule,
        timeBlocks: updatedBlocks,
        completedMinutes,
      };

      set(state => ({
        schedules: { ...state.schedules, [scheduleId]: updated },
      }));
      return updated;
    },

    markEnrichmentComplete: (scheduleId, enrichmentId) => {
      const schedule = get().schedules[scheduleId];
      if (!schedule) return null;

      const updatedSlots = schedule.enrichmentSlots.map(slot => {
        if (slot.id !== enrichmentId) return slot;
        return { ...slot, status: 'completed' as const, completedAt: new Date().toISOString() };
      });

      const inlineCompleted = updatedSlots.filter(s => s.placement === 'inline' && s.status === 'completed').length;
      const blockCompleted = schedule.timeBlocks.filter(b =>
        b.activities.every(a => a.status === 'completed' || a.status === 'skipped')
      ).length;

      const inlineTotal = updatedSlots.filter(s => s.placement === 'inline' && s.status !== 'skipped').length;

      const updated: DailySchedule = {
        ...schedule,
        enrichmentSlots: updatedSlots,
        completedItems: blockCompleted + inlineCompleted,
        totalItems: schedule.timeBlocks.length + inlineTotal,
      };

      set(state => ({
        schedules: { ...state.schedules, [scheduleId]: updated },
      }));
      return updated;
    },

    skipEnrichment: (scheduleId, enrichmentId) => {
      const schedule = get().schedules[scheduleId];
      if (!schedule) return null;

      const updatedSlots = schedule.enrichmentSlots.map(slot => {
        if (slot.id !== enrichmentId) return slot;
        return { ...slot, status: 'skipped' as const };
      });

      const inlineActive = updatedSlots.filter(s => s.placement === 'inline' && s.status !== 'skipped').length;
      const inlineCompleted = updatedSlots.filter(s => s.placement === 'inline' && s.status === 'completed').length;
      const blockCompleted = schedule.timeBlocks.filter(b =>
        b.activities.every(a => a.status === 'completed' || a.status === 'skipped')
      ).length;

      const updated: DailySchedule = {
        ...schedule,
        enrichmentSlots: updatedSlots,
        totalItems: schedule.timeBlocks.length + inlineActive,
        completedItems: blockCompleted + inlineCompleted,
      };

      set(state => ({
        schedules: { ...state.schedules, [scheduleId]: updated },
      }));
      return updated;
    },
  }),
  {
    name: 'homeschool-schedules',
    version: 1,
    // Pass-through migration. Schema has been stable since v0; future schema
    // changes must bump this version and add a real migration branch.
    migrate: (state) => state,
  })
);
